// Builds the itemized, category-grouped view of the user's current selections
// for the pre-submit review in the Send Inquiry modal. It mirrors the server's
// inquiry snapshot (see buildSelectionSnapshot in inquiry-submission.js) and the
// customer confirmation email so what the user verifies here matches what they
// later receive: products grouped by category, each with its selected options,
// and any auto-included products nested beneath their parent.
//
// The included set is derived the same way the server derives it — from the
// catalog's flat include rows for the currently-selected parents — so it stays
// consistent with pricing (auto-included products are free and never listed as
// their own top-level entry).
import { groupIncludesByParent } from './product-includes.js';

// catalog: { categories, products, options, includes } from /api/catalog.
// items:   [{ productId, optionIds: number[] }] from the selections slice.
// Returns: [{ categoryName, categoryOrder, products: [{
//   productId, name, customQuote,
//   options:  [{ optionId, name, customQuote }],
//   included: [{ productId, name, options: [{ optionId, name }] }],
// }] }] sorted by category display order, products in selection order.
export const buildSelectionSummary = (catalog, items) => {
  if (!catalog || !Array.isArray(items) || items.length === 0) return [];

  const categories = catalog.categories || [];
  const products = catalog.products || [];
  const options = catalog.options || [];
  const includeRows = catalog.includes || [];

  const productById = new Map(products.map((p) => [p.id, p]));
  const optionById = new Map(options.map((o) => [o.id, o]));
  const categoryById = new Map(categories.map((c) => [c.id, c]));

  const selectedProductIds = items
    .map((it) => it.productId)
    .filter((id) => productById.has(id));

  // Auto-included products, grouped by the selected parent that brings them in.
  // Non-transitive: only direct includes of selected products are considered.
  const grouped = groupIncludesByParent(includeRows);
  const includedProductIds = new Set();
  const includedByParent = new Map();
  selectedProductIds.forEach((parentId) => {
    const children = grouped.get(parentId);
    if (!children) return;
    children.forEach((child) => {
      includedProductIds.add(child.includedProductId);
      const childProduct = productById.get(child.includedProductId);
      if (!childProduct) return;
      if (!includedByParent.has(parentId)) includedByParent.set(parentId, []);
      includedByParent.get(parentId).push({
        productId: child.includedProductId,
        name: childProduct.name,
        options: child.optionIds
          .map((oid) => optionById.get(oid))
          .filter(Boolean)
          .map((o) => ({ optionId: o.id, name: o.name })),
      });
    });
  });

  // One entry per user-selected product, skipping any that are themselves
  // auto-included by another selected product (shown nested under the parent).
  const entries = items
    .filter((it) => productById.has(it.productId))
    .filter((it) => !includedProductIds.has(it.productId))
    .map((it) => {
      const product = productById.get(it.productId);
      const selectedOptionIds = Array.isArray(it.optionIds) ? it.optionIds : [];
      const category = categoryById.get(product.category_id);
      return {
        productId: product.id,
        name: product.name,
        categoryId: product.category_id ?? null,
        categoryName: category?.name || 'Products',
        categoryOrder: Number.isFinite(category?.display_order)
          ? category.display_order : Number.MAX_SAFE_INTEGER,
        customQuote: Boolean(product.customQuote),
        options: options
          .filter((o) => o.product_id === product.id && selectedOptionIds.includes(o.id))
          .map((o) => ({ optionId: o.id, name: o.name, customQuote: Boolean(o.customQuote) })),
        included: includedByParent.get(it.productId) || [],
      };
    });

  // Group by category, preserving category display order then selection order.
  const groups = new Map();
  entries.forEach((entry) => {
    const key = entry.categoryId ?? '__none__';
    if (!groups.has(key)) {
      groups.set(key, {
        categoryName: entry.categoryName,
        categoryOrder: entry.categoryOrder,
        products: [],
      });
    }
    groups.get(key).products.push(entry);
  });

  return [...groups.values()].sort((a, b) => a.categoryOrder - b.categoryOrder);
};
